import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { OneUseLink, Form } from '../types/form';
import { Copy, Trash2, Plus, ExternalLink, UserCheck, Clock, AlertCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { formsApi } from '../api/api';

interface OneUseLinksProps {
  formId: string;
  form?: Form;
}

const OneUseLinks: React.FC<OneUseLinksProps> = ({ formId, form }) => {
  const navigate = useNavigate();
  const [links, setLinks] = useState<OneUseLink[]>([]);
  const [loading, setLoading] = useState(true);        // first page
  const [loadingMore, setLoadingMore] = useState(false); // subsequent pages
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<{ id: string, name: string } | null>(null);

  // Sentinel observed for infinite scroll, plus an in-flight guard so a fast
  // scroll (or overlapping observer callbacks) can't fire the same page twice.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const inFlightRef = useRef(false);

  const fetchPage = useCallback(
    async (pageToLoad: number, { replace = false, isRetry = false } = {}) => {
      if (!formId || inFlightRef.current) return;
      inFlightRef.current = true;

      try {
        if (replace) setLoading(true);
        else setLoadingMore(true);
        setError(null);

        const response = await formsApi.getOneUseLinks(formId, pageToLoad);
        const data = response.data as any;
        // Paginated envelope ({results, next}); fall back to a bare array in
        // case pagination is ever disabled server-side.
        const results: OneUseLink[] = Array.isArray(data) ? data : (data.results || []);
        const next: string | null = Array.isArray(data) ? null : data.next;

        setLinks(prev => (replace ? results : [...prev, ...results]));
        setHasMore(!!next);
        setPage(pageToLoad);
      } catch (error: any) {
        console.error('Error fetching Custom Links:', error);
        const errorMessage = error.response?.data?.detail || error.message || 'Failed to load Custom Links';
        setError(errorMessage);
        if (replace) setLinks([]);
        setHasMore(false);
        // Only show notification on first error, not retries
        if (!isRetry) {
          toast.error(errorMessage);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
        inFlightRef.current = false;
      }
    },
    [formId]
  );

  // Initial load + reset whenever the form changes.
  useEffect(() => {
    setLinks([]);
    setPage(1);
    setHasMore(false);
    fetchPage(1, { replace: true });
  }, [formId, fetchPage]);

  // Infinite scroll: load the next page when the sentinel scrolls into view.
  useEffect(() => {
    if (!hasMore || loading) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !inFlightRef.current) {
          fetchPage(page + 1);
        }
      },
      { rootMargin: '120px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, page, fetchPage]);

  // Retry handler for the error state — re-fetch from the first page.
  const fetchLinks = (isRetry = false) => {
    setLinks([]);
    setPage(1);
    setHasMore(false);
    fetchPage(1, { replace: true, isRetry });
  };

  const handleDeleteClick = (linkId: string, linkName: string) => {
    setLinkToDelete({ id: linkId, name: linkName });
    setIsDeleteDialogOpen(true);
  };

  const deleteLink = async () => {
    if (!linkToDelete) return;

    try {
      await formsApi.deleteOneUseLink(linkToDelete.id);
      setLinks(prev => {
        const currentLinks = Array.isArray(prev) ? prev : [];
        return currentLinks.filter(link => link.id !== linkToDelete.id);
      });
      toast.success('One-use link deleted successfully');
      setIsDeleteDialogOpen(false);
      setLinkToDelete(null);
    } catch (error: any) {
      console.error('Error deleting one-use link:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to delete one-use link';
      toast.error(errorMessage);
    }
  };

  const copyLink = (token: string) => {
    const link = `${window.location.origin}/public/${token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFieldLabel = (fieldId: string) => {
    if (!form) return fieldId;
    const field = form.fields.find(f => f.id === fieldId);
    return field ? field.label : fieldId;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Custom Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <p className="text-muted-foreground">Loading links...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Custom Links
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h3 className="text-lg font-medium mb-2">Error Loading Links</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button 
              variant="outline" 
              onClick={() => fetchLinks(true)}
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <UserCheck className="h-5 w-5" />
            Custom Links
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Create new link */}
          <div className="flex justify-between items-center">
            <p className="text-sm text-muted-foreground">
              Create custom links with pre-filled form data for specific recipients. Your form remains publicly accessible via the standard link.
            </p>
            <Button 
              onClick={() => navigate(`/forms/${formId}/create-link`)} 
              disabled={!!error}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Create Link
            </Button>
          </div>

          {/* Links list */}
          {!Array.isArray(links) || links.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <UserCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No Custom Links created yet.</p>
              <p className="text-sm">Create links above to share with specific users.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {links.map((link) => (
                <div
                  key={link.id}
                  className={`border rounded-lg p-4 ${
                    link.is_used ? 'bg-muted/50 border-muted' : 'bg-background border-border'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-sm truncate">{link.name}</h4>
                        <span className="text-xs text-muted-foreground">
                          ({link.current_uses}/{link.max_uses} uses)
                        </span>
                        {link.is_used ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-destructive/10 text-destructive text-xs rounded-full">
                            <UserCheck className="h-3 w-3" />
                            Exhausted
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400 text-xs rounded-full">
                            <Clock className="h-3 w-3" />
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Created: {formatDate(link.created_at)}
                        {link.used_at && (
                          <span className="ml-2">
                            • Last used: {formatDate(link.used_at)}
                          </span>
                        )}
                      </p>
                      
                      {/* Pre-selected fields indicator */}
                      {link.pre_selected_fields && Object.keys(link.pre_selected_fields).length > 0 && (
                        <div className="mt-2">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Settings className="h-3 w-3" />
                            <span>
                              {Object.keys(link.pre_selected_fields).length} pre-filled field{Object.keys(link.pre_selected_fields).length !== 1 ? 's' : ''}:
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {Object.entries(link.pre_selected_fields).slice(0, 3).map(([fieldId, value]) => (
                              <span 
                                key={fieldId}
                                className="inline-flex items-center px-2 py-1 bg-primary/10 text-primary text-xs rounded-full"
                              >
                                {getFieldLabel(fieldId)}
                              </span>
                            ))}
                            {Object.keys(link.pre_selected_fields).length > 3 && (
                              <span className="text-xs text-muted-foreground">
                                +{Object.keys(link.pre_selected_fields).length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 mt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyLink(link.token)}
                        className="h-8 px-2 text-xs"
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy Link
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(link.id, link.name)}
                        className="h-8 px-2 text-xs text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                  
                  {!link.is_used && (
                    <div className="mt-3 pt-3 border-t border-border">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <ExternalLink className="h-3 w-3" />
                        <code className="bg-muted px-1 py-0.5 rounded text-[10px] font-mono truncate">
                          {window.location.origin}/public/{link.token}
                        </code>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Infinite-scroll sentinel + loading indicator */}
              {hasMore && (
                <div ref={sentinelRef} className="py-3 text-center">
                  {loadingMore && (
                    <p className="text-sm text-muted-foreground">Loading more links...</p>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete One-Use Link</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the link "{linkToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteLink}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OneUseLinks;